import { useRef, useState, useEffect } from "react";
import { Eraser, Download, Paintbrush } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";

const COLORS = ["#F59E0B", "#FDE68A", "#EF4444", "#22C55E", "#3B82F6", "#EC4899", "#A855F7", "#FFFFFF", "#0A0D17"];
const SIZES = [4, 10, 20];

export const ColoringCanvas = () => {
  const { t } = useLang();
  const s = t.games.canvas;
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [color, setColor] = useState("#F59E0B");
  const [size, setSize] = useState(10);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = canvas.offsetWidth * dpr;
    canvas.height = canvas.offsetHeight * dpr;
    ctx.scale(dpr, dpr);
    ctx.fillStyle = "#111625";
    ctx.fillRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - rect.left, y: p.clientY - rect.top };
  };

  const start = (e) => {
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.strokeStyle = color;
    ctx.lineWidth = size;
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.fillStyle = "#111625";
    ctx.fillRect(0, 0, canvasRef.current.offsetWidth, canvasRef.current.offsetHeight);
  };

  const save = () => {
    const link = document.createElement("a");
    link.download = "my-celestia-art.png";
    link.href = canvasRef.current.toDataURL();
    link.click();
  };

  return (
    <div data-testid="game-canvas">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {COLORS.map((c) => (
          <button
            key={c}
            onClick={() => setColor(c)}
            data-testid={`color-${c}`}
            className={`h-7 w-7 rounded-full border-2 transition-transform hover:scale-110 ${color === c ? "border-gold-light scale-110" : "border-white/20"}`}
            style={{ backgroundColor: c }}
            aria-label={`color ${c}`}
          />
        ))}
        <span className="mx-1 h-6 w-px bg-white/15" />
        {SIZES.map((sz) => (
          <button key={sz} onClick={() => setSize(sz)} className={`flex h-8 w-8 items-center justify-center rounded-full ${size === sz ? "bg-gold-primary/30 text-gold-light" : "text-white/50"}`}>
            <Paintbrush style={{ width: 10 + sz / 3, height: 10 + sz / 3 }} />
          </button>
        ))}
      </div>

      <canvas
        ref={canvasRef}
        data-testid="coloring-canvas"
        className="h-64 w-full cursor-crosshair touch-none rounded-2xl border border-white/12 sm:h-72"
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
      />

      <div className="mt-4 flex justify-center gap-3">
        <button onClick={clear} data-testid="canvas-clear" className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm text-white/80 hover:text-gold-light">
          <Eraser className="h-4 w-4" /> {s.clear}
        </button>
        <button onClick={save} data-testid="canvas-save" className="flex items-center gap-2 rounded-full bg-gradient-to-r from-gold-light to-gold-primary px-5 py-2.5 text-sm font-medium text-ink-900">
          <Download className="h-4 w-4" /> {s.save}
        </button>
      </div>
    </div>
  );
};
